import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  useInviterCreatorRequests,
  useInviterRequesterDirectory,
} from '@/hooks/useInviterCreatorRequests';
import { EmptyState } from '@/components/ui/empty-state';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Pill, toneFor } from '@/components/ui/pill';
import { requestStatusLabel } from '@/lib/labels';
import { PlatformIcon } from '@/components/PlatformIcon';
import { Tag, channelToneFor, channelLabel } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { firstNameOf } from '@/lib/displayName';
import { cn } from '@/lib/utils';
import {
  InviterCreatorRequestBatchesPanel,
  REQUEST_TYPE_LABELS,
  useNicheNames,
} from '@/pages/CreatorRequestBatches';
import type { CreatorRequestStatus, InviteChannel } from '@/api/types';

type RequestsTab = 'individual' | 'batches';

// Active queue (default) — exclude COMPLETED + CANCELLED so the outreacher
// doesn't see their closed work on every visit.
const DEFAULT_ACTIVE_STATUSES = 'REQUESTED,IN_PROGRESS,AWAITING_RESPONSE,AWAITING_RATE,ROSTERED';

const STATUSES: CreatorRequestStatus[] = [
  'REQUESTED',
  'IN_PROGRESS',
  'AWAITING_RESPONSE',
  'AWAITING_RATE',
  'ROSTERED',
  'COMPLETED',
  'CANCELLED',
];

import { INVITE_CHANNELS } from '@/api/types';
const CHANNELS = INVITE_CHANNELS;

const PAGE_SIZE = 50;

export function InviterCreatorRequestsPage(): JSX.Element {
  const [tab, setTab] = useState<RequestsTab>('individual');
  const [status, setStatus] = useState<CreatorRequestStatus | ''>('');
  const [channel, setChannel] = useState<InviteChannel | ''>('');
  const [requesterExternalId, setRequesterExternalId] = useState<string>('');
  const [page, setPage] = useState(1);

  const requestersQuery = useInviterRequesterDirectory();
  const nicheName = useNicheNames();

  const query = useInviterCreatorRequests({
    status: status || DEFAULT_ACTIVE_STATUSES,
    ...(channel ? { channel } : {}),
    ...(requesterExternalId ? { requesterExternalId } : {}),
    page,
    limit: PAGE_SIZE,
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  function resetPage<T>(setter: (v: T) => void): (v: T) => void {
    return (v) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Requests"
        description="Creator and niche requests for your team. Pick one up, move it through the funnel, and keep the campaign manager in the loop with comments."
      />

      <div className="flex gap-1 border-b" role="tablist" aria-label="Request views">
        <TabButton
          active={tab === 'individual'}
          onClick={() => setTab('individual')}
        >
          Individual
        </TabButton>
        <TabButton active={tab === 'batches'} onClick={() => setTab('batches')}>
          Batches
        </TabButton>
      </div>

      {tab === 'batches' ? <InviterCreatorRequestBatchesPanel /> : (
        <>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="grid gap-1.5">
          <Label>Status</Label>
          <Combobox
            value={status}
            onChange={resetPage((v) => setStatus(v as CreatorRequestStatus | ''))}
            options={STATUSES.map((s) => ({ value: s, label: requestStatusLabel(s) }))}
            allowAll
            allLabel="Active (default)"
            placeholder="Active (default)"
          />
        </div>
        <div className="grid gap-1.5">
          <Label>Campaign manager</Label>
          <Combobox
            value={requesterExternalId}
            onChange={resetPage((v) => setRequesterExternalId(v))}
            options={(requestersQuery.data ?? []).map((r) => ({
              value: r.requesterExternalId,
              label: `${firstNameOf(r.requesterName) || r.requesterName} (${r.requestCount})`,
              hint: r.requesterEmail,
            }))}
            allowAll
            allLabel="All campaign managers"
            placeholder="All campaign managers"
          />
        </div>
        <div className="grid gap-1.5">
          <Label>Channel</Label>
          <Combobox
            value={channel}
            onChange={resetPage((v) => setChannel(v as InviteChannel | ''))}
            options={CHANNELS.map((c) => ({ value: c, label: channelLabel(c) }))}
            allowAll
            allLabel="Any channel"
            placeholder="Any channel"
          />
        </div>
      </div>

      {query.isLoading ? (
        <TableSkeleton cols={['w-40', 'w-16', 'w-16', 'w-16', 'w-16']} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="No requests match."
          hint="New ones from campaign managers land here as soon as they're filed."
        />
      ) : (
        <DataTable>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Creator or niche</DataTableHeaderCell>
              <DataTableHeaderCell>Channel</DataTableHeaderCell>
              <DataTableHeaderCell>Status</DataTableHeaderCell>
              <DataTableHeaderCell>Campaign manager</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Age</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {rows.map((r) => (
              <DataTableRow key={r.id}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {r.requestType === 'CREATOR' && r.channel && (
                      <PlatformIcon
                        platform={r.channel}
                        size={14}
                        className="text-muted-foreground"
                      />
                    )}
                    <Link
                      to={`/portal/creator-requests/${r.publicId}`}
                      className="font-medium text-foreground hover:underline"
                    >
                      {r.requestType === 'CREATOR'
                        ? r.targetIdentifier
                        : r.niches.length > 0
                          ? r.niches.map(nicheName).join(' + ')
                          : 'Niche request'}
                    </Link>
                    <Tag tone={r.requestType === 'NICHE' ? 'niche' : 'creator'}>
                      {REQUEST_TYPE_LABELS[r.requestType]}
                    </Tag>
                    {r.requestType === 'NICHE' && r.minFollowers ? (
                      <span className="text-[11px] text-muted-foreground">
                        {r.minFollowers.toLocaleString()}+ followers
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-2" data-label="Channel">
                  {r.channel ? (
                    <Tag tone={channelToneFor(r.channel)}>
                      {channelLabel(r.channel)}
                    </Tag>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2" data-label="Status">
                  <Pill tone={toneFor(r.status)}>{requestStatusLabel(r.status)}</Pill>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground" data-label="Campaign manager">
                  {firstNameOf(r.requesterName) || r.requesterName}
                </td>
                <td className="px-3 py-2 text-right text-xs text-muted-foreground" data-label="Age">
                  {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
                </td>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
      )}

      <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
